import React, { useState } from 'react';
import { collection, doc, setDoc } from 'firebase/firestore';
import { db } from '../services/firebase.js';
import { CheckCircle, AlertCircle, Loader } from 'lucide-react';

// 30 Initial Emojis - Same as seed script
const EMOJIS = [
    // === GLOBAL TIER (Free - 10 emojis) ===
    { id: 'heart', name: 'Heart', emoji: '❤️', category: 'love', tier: 'global', sortOrder: 1 },
    { id: 'fire', name: 'Fire', emoji: '🔥', category: 'celebration', tier: 'global', sortOrder: 2 },
    { id: 'sparkles', name: 'Sparkles', emoji: '✨', category: 'creativity', tier: 'global', sortOrder: 3 },
    { id: 'clap', name: 'Clap', emoji: '👏', category: 'appreciation', tier: 'global', sortOrder: 4 },
    { id: 'palette', name: 'Palette', emoji: '🎨', category: 'creativity', tier: 'global', sortOrder: 5 },
    { id: 'lightbulb', name: 'Lightbulb', emoji: '💡', category: 'inspiration', tier: 'global', sortOrder: 6 },
    { id: 'rocket', name: 'Rocket', emoji: '🚀', category: 'motivation', tier: 'global', sortOrder: 7 },
    { id: 'star', name: 'Star', emoji: '⭐', category: 'appreciation', tier: 'global', sortOrder: 8 },
    { id: 'heart_eyes', name: 'Heart Eyes', emoji: '😍', category: 'love', tier: 'global', sortOrder: 9 },
    { id: 'party', name: 'Party Popper', emoji: '🎉', category: 'celebration', tier: 'global', sortOrder: 10 },

    // === SUBSCRIBER TIER ($5/month - 10 emojis) ===
    { id: 'crown', name: 'Crown', emoji: '👑', category: 'premium', tier: 'subscriber', sortOrder: 11 },
    { id: 'diamond', name: 'Diamond', emoji: '💎', category: 'premium', tier: 'subscriber', sortOrder: 12 },
    { id: 'trophy', name: 'Trophy', emoji: '🏆', category: 'achievement', tier: 'subscriber', sortOrder: 13 },
    { id: 'rainbow', name: 'Rainbow', emoji: '🌈', category: 'creativity', tier: 'subscriber', sortOrder: 14 },
    { id: 'unicorn', name: 'Unicorn', emoji: '🦄', category: 'creativity', tier: 'subscriber', sortOrder: 15 },
    { id: 'lightning', name: 'Lightning', emoji: '⚡', category: 'motivation', tier: 'subscriber', sortOrder: 16 },
    { id: 'blossom', name: 'Blossom', emoji: '🌸', category: 'appreciation', tier: 'subscriber', sortOrder: 17 },
    { id: 'theater', name: 'Theater Masks', emoji: '🎭', category: 'creativity', tier: 'subscriber', sortOrder: 18 },
    { id: 'circus', name: 'Circus Tent', emoji: '🎪', category: 'celebration', tier: 'subscriber', sortOrder: 19 },
    { id: 'crystal_ball', name: 'Crystal Ball', emoji: '🔮', category: 'inspiration', tier: 'subscriber', sortOrder: 20 },

    // === SUPER SUBSCRIBER TIER ($10/month - 10 emojis) ===
    { id: 'galaxy', name: 'Milky Way', emoji: '🌌', category: 'premium', tier: 'superSubscriber', sortOrder: 21 },
    { id: 'fireworks', name: 'Fireworks', emoji: '🎆', category: 'celebration', tier: 'superSubscriber', sortOrder: 22 },
    { id: 'alien', name: 'Alien', emoji: '👾', category: 'creativity', tier: 'superSubscriber', sortOrder: 23 },
    { id: 'musical_notes', name: 'Musical Notes', emoji: '🎵', category: 'creativity', tier: 'superSubscriber', sortOrder: 24 },
    { id: 'frame', name: 'Framed Picture', emoji: '🖼️', category: 'appreciation', tier: 'superSubscriber', sortOrder: 25 },
    { id: 'medal', name: 'Medal', emoji: '🏅', category: 'achievement', tier: 'superSubscriber', sortOrder: 26 },
    { id: 'gem_stone', name: 'Gem Stone', emoji: '💠', category: 'premium', tier: 'superSubscriber', sortOrder: 27 },
    { id: 'comet', name: 'Comet', emoji: '☄️', category: 'motivation', tier: 'superSubscriber', sortOrder: 28 },
    { id: 'dizzy', name: 'Dizzy', emoji: '💫', category: 'inspiration', tier: 'superSubscriber', sortOrder: 29 },
    { id: 'magic_wand', name: 'Magic Wand', emoji: '🪄', category: 'inspiration', tier: 'superSubscriber', sortOrder: 30 },
];

export default function SeedEmojis({ user, onNavigate }) {
    const [status, setStatus] = useState('ready'); // ready, seeding, success, error
    const [progress, setProgress] = useState(0);
    const [message, setMessage] = useState('');

    const seedEmojis = async () => {
        if (!user?.isAdmin && !user?.isSuperAdmin) {
            setStatus('error');
            setMessage('❌ Admin access required to seed emojis');
            return;
        }

        setStatus('seeding');
        setProgress(0);

        try {
            for (let i = 0; i < EMOJIS.length; i++) {
                const emojiData = EMOJIS[i];
                const emojiDoc = doc(db, 'emojis', emojiData.id);

                await setDoc(emojiDoc, {
                    ...emojiData,
                    imageUrl: null, // Will be populated later with custom images
                    usageCount: 0,
                    isActive: true,
                    createdAt: new Date()
                });

                setProgress(Math.round(((i + 1) / EMOJIS.length) * 100));
            }

            setStatus('success');
            setMessage(`✅ Successfully seeded ${EMOJIS.length} emojis!`);
        } catch (error) {
            console.error('Error seeding emojis:', error);
            setStatus('error');
            setMessage(`❌ Error: ${error.message}`);
        }
    };

    if (!user?.isAdmin && !user?.isSuperAdmin) {
        return (
            <div className="animate-fade-in" style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 20px' }}>
                <div className="glass-panel" style={{ padding: '40px', textAlign: 'center' }}>
                    <AlertCircle size={48} color="#ef4444" style={{ marginBottom: '20px' }} />
                    <h2>Access Denied</h2>
                    <p style={{ color: '#999', marginTop: '10px' }}>
                        This page is only accessible to administrators.
                    </p>
                    <button onClick={() => onNavigate('feed')} className="btn" style={{ marginTop: '20px' }}>
                        Back to Feed
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="animate-fade-in" style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 20px' }}>
            <div className="glass-panel" style={{ padding: '40px' }}>
                <h1 style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    🎨 Emoji Database Seeder
                </h1>
                <p style={{ color: '#999', marginBottom: '30px' }}>
                    Click the button below to populate the database with 30 initial emojis.
                </p>

                {/* Progress Section */}
                {status === 'seeding' && (
                    <div style={{ marginBottom: '30px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                            <Loader className="spin" size={20} color="#8b5cf6" />
                            <span>Seeding emojis... {progress}%</span>
                        </div>
                        <div style={{
                            width: '100%',
                            height: '8px',
                            background: 'rgba(255,255,255,0.1)',
                            borderRadius: '4px',
                            overflow: 'hidden'
                        }}>
                            <div style={{
                                width: `${progress}%`,
                                height: '100%',
                                background: 'linear-gradient(90deg, #8b5cf6, #ec4899)',
                                transition: 'width 0.3s ease'
                            }} />
                        </div>
                    </div>
                )}

                {/* Status Message */}
                {message && (
                    <div style={{
                        padding: '15px',
                        borderRadius: '8px',
                        background: status === 'success' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                        border: `1px solid ${status === 'success' ? '#22c55e' : '#ef4444'}`,
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                    }}>
                        {status === 'success' ?
                            <CheckCircle size={20} color="#22c55e" /> :
                            <AlertCircle size={20} color="#ef4444" />
                        }
                        <span>{message}</span>
                    </div>
                )}

                {/* Emoji Preview */}
                <div style={{ marginBottom: '30px' }}>
                    <h3 style={{ fontSize: '14px', color: '#999', marginBottom: '15px' }}>Preview (30 Emojis)</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' }}>
                        {/* Global */}
                        <div className="glass-panel" style={{ padding: '15px' }}>
                            <div style={{ fontSize: '12px', color: '#8b5cf6', fontWeight: 'bold', marginBottom: '10px' }}>
                                🌍 GLOBAL (10)
                            </div>
                            <div style={{ fontSize: '24px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                {EMOJIS.filter(e => e.tier === 'global').map(e => (
                                    <span key={e.id} title={e.name}>{e.emoji}</span>
                                ))}
                            </div>
                        </div>

                        {/* Subscriber */}
                        <div className="glass-panel" style={{ padding: '15px' }}>
                            <div style={{ fontSize: '12px', color: '#ec4899', fontWeight: 'bold', marginBottom: '10px' }}>
                                💎 SUBSCRIBER (10)
                            </div>
                            <div style={{ fontSize: '24px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                {EMOJIS.filter(e => e.tier === 'subscriber').map(e => (
                                    <span key={e.id} title={e.name}>{e.emoji}</span>
                                ))}
                            </div>
                        </div>

                        {/* Super Subscriber */}
                        <div className="glass-panel" style={{ padding: '15px' }}>
                            <div style={{ fontSize: '12px', color: '#f59e0b', fontWeight: 'bold', marginBottom: '10px' }}>
                                👑 SUPER (10)
                            </div>
                            <div style={{ fontSize: '24px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                {EMOJIS.filter(e => e.tier === 'superSubscriber').map(e => (
                                    <span key={e.id} title={e.name}>{e.emoji}</span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                        onClick={seedEmojis}
                        disabled={status === 'seeding' || status === 'success'}
                        className="btn"
                        style={{ flex: 1 }}
                    >
                        {status === 'seeding' ? 'Seeding...' : status === 'success' ? '✅ Complete' : '🚀 Seed 30 Emojis'}
                    </button>
                    <button onClick={() => onNavigate('feed')} className="btn-ghost">
                        Back to Feed
                    </button>
                </div>
            </div>
        </div>
    );
}
